import React from 'react';
import styled, {keyframes} from 'styled-components';
import {D} from './design-tokens';

const spin = keyframes`
	to { transform: rotate(360deg); }
`;

const bounce = keyframes`
	0%, 80%, 100% { transform: translateY(0); opacity: 0.3; }
	40% { transform: translateY(-4px); opacity: 1; }
`;

// ── SmallBtn ──────────────────────────────────────────────────────────────────

type SmallBtnProps = {
	children: React.ReactNode;
	onClick: () => void;
};

const SmallBtnEl = styled.button`
	padding: 5px 16px;
	font-size: 12px;
	cursor: pointer;
	border-radius: 6px;
	background: ${D.surface2};
	border: 1px solid ${D.userBubbleBorder};
	color: ${D.accentMid};
	font-weight: 600;
	transition: background 0.15s, border-color 0.15s;

	&:hover {
		background: ${D.userBubbleBorder};
		border-color: ${D.accentMid};
	}
`;

export function SmallBtn({children, onClick}: SmallBtnProps): JSX.Element {
	return (
		<SmallBtnEl type="button" onClick={onClick}>
			{children}
		</SmallBtnEl>
	);
}

// ── ThinkingDots ──────────────────────────────────────────────────────────────

const DotsWrapper = styled.div`
	display: flex;
	gap: 4px;
	padding: 3px 0;
`;

const Dot = styled.div<{delay: number}>`
	width: 5px;
	height: 5px;
	border-radius: 50%;
	background: ${D.accentMid};
	opacity: 0.4;
	animation: ${bounce} 1.2s ${({delay}) => delay}s infinite;
`;

export function ThinkingDots(): JSX.Element {
	return (
		<DotsWrapper>
			{[0, 1, 2].map(i => (
				<Dot key={i} delay={i * 0.2} />
			))}
		</DotsWrapper>
	);
}

// ── CheckIcon (20px — done state) ─────────────────────────────────────────────

export function CheckIcon(): JSX.Element {
	return (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{flexShrink: 0}}>
			<circle cx="10" cy="10" r="9" stroke="#148f6a" strokeWidth="1.5" fill="none" />
			<path d="M6 10l3 3 5-5" stroke="#148f6a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

// ── SpinnerIcon (20px — active state) ─────────────────────────────────────────

const SpinnerSvg = styled.svg`
	animation: ${spin} 0.8s linear infinite;
	flex-shrink: 0;
`;

export function SpinnerIcon(): JSX.Element {
	// circumference of r=8 circle ≈ 50.3 — dash of ~38 gives a 3/4 arc gap of ~12
	return (
		<SpinnerSvg width="20" height="20" viewBox="0 0 20 20" fill="none">
			<circle cx="10" cy="10" r="8" stroke="#d3d3df" strokeWidth="1.5" />
			<circle
				cx="10"
				cy="10"
				r="8"
				stroke="#0004db"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeDasharray="38 13"
				strokeDashoffset="0"
			/>
		</SpinnerSvg>
	);
}

// ── IdleIcon (20px — idle/queued state) ───────────────────────────────────────

export function IdleIcon(): JSX.Element {
	return (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{flexShrink: 0}}>
			<circle cx="10" cy="10" r="9" stroke="#d3d3df" strokeWidth="1.5" fill="none" />
		</svg>
	);
}

// ── SparkleIcon ───────────────────────────────────────────────────────────────

type SparkleIconProps = {
	size?: number;
	color?: string;
};

export function SparkleIcon({size = 16, color = 'currentColor'}: SparkleIconProps): JSX.Element {
	return (
		<svg width={size} height={size} viewBox="0 0 16 16" fill="none">
			<path d="M8 2l1.2 3.8H13L9.9 7.9l1.2 3.8L8 9.6l-3.1 2.1 1.2-3.8L3 5.8h3.8L8 2z" fill={color} />
		</svg>
	);
}

// ── CloseIcon ─────────────────────────────────────────────────────────────────

export function CloseIcon(): JSX.Element {
	return (
		<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
			<path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
		</svg>
	);
}

// ── SendIcon ──────────────────────────────────────────────────────────────────

type SendIconProps = {
	active: boolean;
};

export function SendIcon({active}: SendIconProps): JSX.Element {
	return (
		<svg width="13" height="13" viewBox="0 0 13 13" fill="none">
			<path d="M1 12L12 6.5 1 1v4.5l7 2-7 2V12z" fill={active ? '#ffffff' : D.textDisabled} />
		</svg>
	);
}

// ── StopIcon ──────────────────────────────────────────────────────────────────

export function StopIcon(): JSX.Element {
	return (
		<svg width="10" height="10" viewBox="0 0 10 10" fill="none">
			<rect x="1.5" y="1.5" width="7" height="7" rx="1" fill={D.errorText} />
		</svg>
	);
}
